import "./MetaDocPage.scss";

import { BackgroundJobsDrawer } from "components/BackgroundJobsDrawer/BackgroundJobsDrawer";
import { KaboodleToaster } from "components/common";
import CodeDirectoryDrawer from "components/CodeDirectory/CodeDirectoryDrawer";
import Documents from "components/Documents/drawer/DocumentsDrawer";
import { useMetadocTable } from "components/Documents/hooks/useMetadocTable";
import Tabs from "components/Tabs/Tabs";
import { FEATURE_FLAGS } from "constants/FeatureFlags";
import { clearCurrentTask } from "reducers/files";
import { setShowTaskModal } from "reducers/main";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { setShowDocumentsDrawer } from "reducers/drawer";
import { setService } from "reducers/future-medical";
import { api } from "services/api";
import { useGetAmendedReviewByIdQuery } from "services/ar-screening-api";
import { useGetCostProjectionByIdQuery } from "services/cost-projection-api";
import { useGetDocumentGroupsQuery } from "services/document-group-api";
import { useGetDocumentGroupTagsQuery } from "services/document-group-tag-api";
import { useGetDocumentManagementFileByIdQuery } from "services/document-management-api";
import { useGetFileByIdQuery } from "services/file-api";
import { useGetMSAByIdQuery } from "services/msa-api";
import type { RootState } from "services/store";
import { useDispatch, useSelector } from "utils/redux";

import * as Sentry from "@sentry/react";

import { createTabs } from "./config/tabConfig";
import { useMetadocComputedValues } from "./hooks/computed/useMetadocComputedValues";
import { useGroupingSource } from "./hooks/page-operations/useGroupingSource";
import { useMetadocAIFlow } from "./hooks/page-operations/useMetadocAIFlow";
import { useMetadocPolling } from "./hooks/page-operations/useMetadocPolling";
import { useResolvedMetadocDownload } from "./hooks/page-operations/useResolvedMetadocDownload";
import { useMetadocState } from "./hooks/state/useMetadocState";
import MetaDocEmptyScreen from "./screens/MetaDocEmptyScreen";
import MetaDocErrorScreen from "./screens/MetaDocErrorScreen";
import MetaDocLoadingScreen from "./screens/MetaDocLoadingScreen";
import MetaDocNoContextScreen from "./screens/MetaDocNoContextScreen";
import MetaDocProcessingScreen from "./screens/MetaDocProcessingScreen";
import {
  getAIModelUserMessage,
  getApiErrorContext,
} from "./utils/metadocErrorUtils";
import {
  getFutureMedicalMetadocContext,
  getMetadocNavigationContext,
} from "./utils/metadocRoutes";
import MetaDocErrorBoundary from "./workspace/MetaDocErrorBoundary";
import MetaDocViewerWorkspace from "./workspace/MetaDocViewerWorkspace";
import FutureMedicalMedicalHistoryReview from "../future-medical/shared/medical-history/FMMedicalHistoryReview";
import FMRxHistory from "../future-medical/shared/medical-history/FMRxHistory";
import TaskModal from "../tasks/components/modals/TaskModal";

import type { DocumentManagementDataType } from "types/DataTypes";
import type { MedicalHistoryTableProps } from "../future-medical/shared/medical-history/medicalRecordUtils";
import type { TabId } from "./metadoc.types";
const MetaDocPage = () => {
  const location = useLocation();
  const {
    fileId,
    documentManagementFileId,
    metadocId,
    msaId,
    mcpId,
    lcpId,
    reviewId,
  } = useParams<{
    fileId?: string;
    documentManagementFileId?: string;
    metadocId?: string;
    msaId?: string;
    mcpId?: string;
    lcpId?: string;
    reviewId?: string;
  }>();
  const dispatch = useDispatch();
  const appEnvironment = import.meta.env.VITE_APP_ENV;
  const isDev = appEnvironment === "local" || appEnvironment === "dev";
  const constants = useSelector((state: RootState) => state.shared.constants);
  const showTaskModal = useSelector(
    (state: RootState) => state.main.showTaskModal,
  );
  const isPostAiEnabled =
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.METADOC_POST_AI_ENABLED];
  const isSummariesMetadataEnabled =
    constants?.FEATURE_FLAGS?.[
      FEATURE_FLAGS.METADOC_SUMMARIES_METADATA_ENABLED
    ];
  const futureMedicalContext = useMemo(
    () =>
      getFutureMedicalMetadocContext(location.pathname, {
        fileId,
        msaId,
        mcpId,
        lcpId,
        reviewId,
      }),
    [location.pathname, fileId, msaId, mcpId, lcpId, reviewId],
  );
  const metadocNavigationContext = useMemo(
    () =>
      getMetadocNavigationContext(location.pathname, {
        fileId,
        documentManagementFileId,
        msaId,
        mcpId,
        lcpId,
        reviewId,
      }),
    [
      location.pathname,
      fileId,
      documentManagementFileId,
      msaId,
      mcpId,
      lcpId,
      reviewId,
    ],
  );
  const isAmendedReviewMetadoc =
    futureMedicalContext?.workspace === "future-medical" &&
    futureMedicalContext.serviceSection === "amended-review";
  const isMsaMetadoc =
    futureMedicalContext?.workspace === "future-medical" &&
    futureMedicalContext.serviceSection === "msa";
  const isCostProjectionMetadoc =
    futureMedicalContext?.workspace === "future-medical" &&
    (futureMedicalContext.serviceSection === "mcp" ||
      futureMedicalContext.serviceSection === "lcp");
  const isArScreeningMetadoc =
    futureMedicalContext?.workspace === "future-medical" &&
    futureMedicalContext.serviceSection === "ar-screening";
  const costProjectionId = mcpId || lcpId;

  const { data: msaService, isLoading: isMsaServiceLoading } =
    useGetMSAByIdQuery(
      { msaId: msaId || "" },
      { skip: !msaId || (!isMsaMetadoc && !isAmendedReviewMetadoc) },
    );
  const {
    data: costProjectionService,
    isLoading: isCostProjectionServiceLoading,
  } = useGetCostProjectionByIdQuery(
    { costProjectionId: costProjectionId || "" },
    { skip: !costProjectionId || !isCostProjectionMetadoc },
  );
  const {
    data: amendedReviewService,
    isLoading: isAmendedReviewServiceLoading,
  } = useGetAmendedReviewByIdQuery(
    { reviewId: reviewId || "" },
    { skip: !reviewId || !isArScreeningMetadoc },
  );
  const currentFutureMedicalService = useMemo(() => {
    if (isArScreeningMetadoc) {
      return amendedReviewService;
    }

    if (isCostProjectionMetadoc) {
      return costProjectionService;
    }

    if (isMsaMetadoc || isAmendedReviewMetadoc) {
      return msaService;
    }

    return undefined;
  }, [
    amendedReviewService,
    costProjectionService,
    isAmendedReviewMetadoc,
    isArScreeningMetadoc,
    isCostProjectionMetadoc,
    isMsaMetadoc,
    msaService,
  ]);
  const medicalHistoryService = useMemo<
    MedicalHistoryTableProps["service"] | undefined
  >(() => {
    if (isCostProjectionMetadoc) {
      return costProjectionService;
    }

    if (isMsaMetadoc || isAmendedReviewMetadoc) {
      return msaService;
    }

    return undefined;
  }, [
    costProjectionService,
    isAmendedReviewMetadoc,
    isCostProjectionMetadoc,
    isMsaMetadoc,
    msaService,
  ]);
  const isFutureMedicalServiceLoading =
    isMsaServiceLoading ||
    isCostProjectionServiceLoading ||
    isAmendedReviewServiceLoading;
  const {
    data: documentManagementService,
    isLoading: isDocumentManagementServiceLoading,
  } = useGetDocumentManagementFileByIdQuery(
    { documentManagementFileId: documentManagementFileId || "" },
    {
      skip:
        !documentManagementFileId ||
        metadocNavigationContext?.workspace !== "document-management",
    },
  );
  const currentDocumentManagementService = useMemo(() => {
    if (!documentManagementService) {
      return undefined;
    }

    return {
      ...documentManagementService,
      claimant: documentManagementService.file?.claimants?.[0] || null,
    } as DocumentManagementDataType;
  }, [documentManagementService]);
  const resolvedFileId =
    fileId ||
    currentDocumentManagementService?.file?.id?.toString() ||
    currentFutureMedicalService?.file?.id?.toString();

  // centralized state management
  const state = useMetadocState({ metadocId });
  const [shouldPollMetadocAfterAISuccess, setShouldPollMetadocAfterAISuccess] =
    useState(false);
  const [metadocSearchValue, setMetadocSearchValue] = useState("");
  const hasRequestedFinalPostAiRefetch = useRef(false);

  // use custom hook for polling metadoc status
  const { metadoc, isMetadocLoading, isMetadocFetching, refetchMetadoc } =
    useMetadocPolling({
      metadocId,
      forcePolling: shouldPollMetadocAfterAISuccess,
    });
  const [shouldPollDocumentGroups, setShouldPollDocumentGroups] =
    useState(false);

  // fetch document groups for this metadoc
  const {
    data: documentGroups = [],
    isLoading: isDocumentGroupsLoading,
    isFetching: isDocumentGroupsFetching,
    refetch: refetchDocumentGroups,
  } = useGetDocumentGroupsQuery(metadoc?.id ?? 0, {
    skip: !metadoc?.id,
    pollingInterval: shouldPollDocumentGroups ? 3000 : 0,
  });

  // fetch document group tags for this metadoc
  const { data: documentGroupTags = [] } = useGetDocumentGroupTagsQuery(
    metadoc?.id ?? 0,
    { skip: !metadoc?.id },
  );

  // bucket document groups by source
  const opsGroups = useMemo(
    () => documentGroups.filter((g) => g.source === "OPS"),
    [documentGroups],
  );
  const aiGroups = useMemo(
    () => documentGroups.filter((g) => g.source === "AI"),
    [documentGroups],
  );
  const hasAIGroups = aiGroups.length > 0;
  const canShowGroupingToggle =
    isPostAiEnabled && (!!metadoc?.pre_ai_run_download_url || hasAIGroups);
  const isWaitingForPostAiArtifacts =
    isPostAiEnabled &&
    shouldPollMetadocAfterAISuccess &&
    !canShowGroupingToggle;

  const { groupingSource, handleGroupingSourceChange, triggerAutoSwitchToAI } =
    useGroupingSource({
      metadocId,
      hasAIGroups,
      isDocumentGroupsLoading,
      isDocumentGroupsFetching,
    });

  // feature flag off -> grouping is forced to OPS (AI toggle ignored)
  const effectiveGroupingSource = isPostAiEnabled ? groupingSource : "OPS";

  const { resolvedDownloadUrl, hasResolvedDownloadUrl } =
    useResolvedMetadocDownload({
      metadoc,
      groupingSource: effectiveGroupingSource,
    });

  // determine active groups based on current grouping source
  const activeGroups = useMemo(
    () => (effectiveGroupingSource === "AI" ? aiGroups : opsGroups),
    [effectiveGroupingSource, aiGroups, opsGroups],
  );
  // fetch file data to get services list
  const { data: fileData } = useGetFileByIdQuery(
    { id: resolvedFileId || "" },
    { skip: !resolvedFileId },
  );

  // fetch metadocs for dashboard view (only when no metadocId - i.e., on dashboard)
  const {
    metadocs,
    totalCount: metadocsCount,
    page: metadocsPage,
    setPage: setMetadocsPage,
    isLoading: isMetadocsLoading,
  } = useMetadocTable({
    fileId: resolvedFileId ? parseInt(resolvedFileId, 10) : undefined,
    skipCondition: !!metadocId, // skip when viewing a specific metadoc
    searchTerm: metadocSearchValue,
  });

  // check if the metadoc data matches the current URL metadocId
  // this prevents showing old cached data when navigating between metadocs
  const isMetadocMismatch =
    metadocId && metadoc && metadoc.id !== parseInt(metadocId, 10);

  // check if any metadoc for this file is currently processing (from Redux)
  const processingMetadocs = useSelector(
    (state: RootState) => state.metadoc.processingMetadocs,
  );

  // if we have a specific metadocId, check if that one is processing
  // if we're on the base page (no metadocId), check if ANY metadoc for this file is processing
  const isProcessingFromRedux = metadocId
    ? processingMetadocs.some(
        (m) =>
          m.metadocId === parseInt(metadocId, 10) &&
          (m.status === "pending" || m.status === "processing"),
      )
    : processingMetadocs.some(
        (m) =>
          m.fileId === parseInt(resolvedFileId || "", 10) &&
          (m.status === "pending" || m.status === "processing"),
      );

  // reset selection when fileId changes (navigating to different file)
  useEffect(() => {
    state.setSelectedDocumentIds(new Set());
  }, [resolvedFileId, state.setSelectedDocumentIds]);

  useEffect(() => {
    if (currentFutureMedicalService) {
      dispatch(setService(currentFutureMedicalService));
    }
  }, [currentFutureMedicalService, dispatch]);

  useEffect(() => {
    if (currentDocumentManagementService) {
      dispatch(setService(currentDocumentManagementService));
    }
  }, [currentDocumentManagementService, dispatch]);

  const handleCloseTaskModal = useCallback(() => {
    dispatch(clearCurrentTask());
    dispatch(setShowTaskModal(false));
  }, [dispatch]);

  const handleOpenDocuments = () => {
    dispatch(setShowDocumentsDrawer(true));
  };

  const handleAIProcessingComplete = useCallback(() => {
    const resolvedMetadocId =
      metadoc?.id ?? (metadocId ? parseInt(metadocId, 10) : null);

    // refetch metadoc data to get AI results
    refetchMetadoc();
    // invalidate metadoc metadata cache so active subscribers refresh and
    // inactive ai tabs fetch fresh data when opened.
    if (resolvedMetadocId) {
      dispatch(
        api.util.invalidateTags([
          { type: "MetadocMetadata", id: `METADOC-${resolvedMetadocId}` },
          { type: "DocumentGroupTags", id: "LIST" },
        ]),
      );
    }
    // keep polling metadoc until post-ai fields are persisted.
    hasRequestedFinalPostAiRefetch.current = false;
    setShouldPollMetadocAfterAISuccess(true);
    // refetch document groups to get AI groups
    refetchDocumentGroups();
    // continue polling groups until AI groups exist, so the toggle appears
    // without requiring a page refresh after success.
    setShouldPollDocumentGroups(true);
    // trigger auto-switch to AI when feature is enabled
    if (isPostAiEnabled) {
      triggerAutoSwitchToAI();
    }
  }, [
    metadoc?.id,
    metadocId,
    dispatch,
    refetchMetadoc,
    refetchDocumentGroups,
    triggerAutoSwitchToAI,
    isPostAiEnabled,
  ]);

  useEffect(() => {
    if (shouldPollDocumentGroups && hasAIGroups) {
      setShouldPollDocumentGroups(false);
    }
  }, [shouldPollDocumentGroups, hasAIGroups]);

  useEffect(() => {
    if (!shouldPollMetadocAfterAISuccess) {
      return;
    }

    if (metadoc?.status === "failed") {
      setShouldPollMetadocAfterAISuccess(false);
      return;
    }

    // stop forced metadoc polling once the ops source url is available.
    if (metadoc?.pre_ai_run_download_url) {
      setShouldPollMetadocAfterAISuccess(false);
      return;
    }

    // once ai groups are visible and metadoc is completed, stop interval polling
    // and do a single final refresh to pick up late-arriving post-ai fields.
    if (metadoc?.status === "completed" && hasAIGroups) {
      setShouldPollMetadocAfterAISuccess(false);

      if (!hasRequestedFinalPostAiRefetch.current) {
        hasRequestedFinalPostAiRefetch.current = true;
        refetchMetadoc();
      }
    }
  }, [
    shouldPollMetadocAfterAISuccess,
    metadoc?.status,
    metadoc?.pre_ai_run_download_url,
    hasAIGroups,
    refetchMetadoc,
  ]);

  const {
    aiModelError,
    backgroundJobsData,
    handleRunAIModel,
    isAIProcessing,
    isRunningAIModel,
  } = useMetadocAIFlow({
    fileId: resolvedFileId,
    metadocId,
    isDev,
    onAIProcessingComplete: handleAIProcessingComplete,
  });

  // handle API mutation errors
  useEffect(() => {
    if (aiModelError) {
      const { status, data } = getApiErrorContext(aiModelError);
      const userMessage = getAIModelUserMessage(aiModelError);

      KaboodleToaster.error(userMessage);

      if (status === 400) {
        return;
      }

      Sentry.captureException(new Error(userMessage), {
        tags: { feature: "metadoc", action: "ai_model_mutation_error" },
        extra: { metadocId, status, data },
      });
    }
  }, [aiModelError, metadocId]);

  // compute derived values and stats
  const computed = useMetadocComputedValues({
    metadoc,
    metadocId,
    opsGroups,
    activeGroups,
    groupingSource: effectiveGroupingSource,
    backgroundJobsData,
    isAIProcessing,
    isRunningAIModel,
  });

  // check for processing state BEFORE showing empty state (check both Redux and API)
  const isProcessing =
    !state.isPageDeletionInProgress &&
    (isProcessingFromRedux ||
      (metadoc &&
        (metadoc.status === "pending" || metadoc.status === "processing")));

  const viewState = useMemo(() => {
    let viewStateValue: string;
    if (isProcessing) {
      viewStateValue = "processing";
    } else if (!metadocId) {
      viewStateValue = "dashboard";
    } else if (isMetadocLoading || isMetadocMismatch) {
      viewStateValue = "loading";
    } else if (isAIProcessing && metadoc) {
      // only show ai-processing if metadoc has loaded
      viewStateValue = "ai-processing";
    } else if (metadoc?.error_message) {
      viewStateValue = "error";
    } else if (isWaitingForPostAiArtifacts) {
      // keep workspace disabled until post-ai artifacts are available.
      viewStateValue = "preparing";
      // view state is gated by the resolved download URL (OPS or AI)
    } else if (hasResolvedDownloadUrl) {
      viewStateValue = "viewer";
    } else {
      viewStateValue = "preparing";
    }

    return viewStateValue;
  }, [
    isProcessing,
    isAIProcessing,
    hasAIGroups,
    metadocId,
    isMetadocLoading,
    isMetadocMismatch,
    metadoc,
    isWaitingForPostAiArtifacts,
    hasResolvedDownloadUrl,
    isProcessingFromRedux,
  ]);

  const pageClass = useMemo(() => {
    const shouldRenderTabsInViewer =
      isSummariesMetadataEnabled && effectiveGroupingSource !== "OPS";

    if (viewState === "viewer") {
      return shouldRenderTabsInViewer
        ? "metadoc-page--ai-results"
        : "metadoc-page--viewer";
    }
    if (viewState === "ai-processing") {
      return "metadoc-page--viewer"; // maintain consistent layout during AI processing
    }
    return "";
  }, [viewState, isSummariesMetadataEnabled, effectiveGroupingSource]);

  if (
    !resolvedFileId &&
    (isFutureMedicalServiceLoading || isDocumentManagementServiceLoading)
  ) {
    return <MetaDocLoadingScreen message="Loading Meta Document..." />;
  }

  if (!resolvedFileId) {
    return <MetaDocNoContextScreen />;
  }

  const parsedFileId = parseInt(resolvedFileId, 10);

  const renderContent = () => {
    switch (viewState) {
      case "processing":
        return <MetaDocProcessingScreen metadoc={metadoc} />;

      case "ai-processing":
        if (!metadoc) {
          return <MetaDocLoadingScreen message="Loading Meta Document..." />;
        }
        return (
          <MetaDocErrorBoundary viewState={viewState} metadocId={metadoc.id}>
            <MetaDocViewerWorkspace
              key={metadoc.id}
              metadoc={metadoc}
              onDeletionStateChange={state.setIsPageDeletionInProgress}
              isProcessing={true}
              processingJobStatus={computed.processingJobStatus}
              onProcessingComplete={handleAIProcessingComplete}
              documentGroups={activeGroups}
              documentGroupTags={documentGroupTags}
              pageClassificationStats={computed.activePageStats}
              onRunAIModel={handleRunAIModel}
              isRunningAIModel={isRunningAIModel}
              isRunAiModelDisabled={computed.isRunAiModelDisabled}
              runAiModelDisabledReason={computed.runAiModelDisabledReason}
              lastCompletedAIJob={computed.lastCompletedAIJob}
              isRunAiModelReady={computed.isRunAiModelReady}
              isMetadocFetching={isMetadocFetching}
              groupingSource={effectiveGroupingSource}
              resolvedDownloadUrl={resolvedDownloadUrl}
              onGroupingSourceChange={handleGroupingSourceChange}
              canShowGroupingToggle={canShowGroupingToggle}
            />
          </MetaDocErrorBoundary>
        );

      case "dashboard":
        return (
          <MetaDocEmptyScreen
            onCreateNew={handleOpenDocuments}
            metadocs={metadocs}
            totalCount={metadocsCount}
            page={metadocsPage}
            onPageChange={setMetadocsPage}
            isLoading={isMetadocsLoading}
            fileId={parsedFileId}
            fileServices={fileData?.services}
            searchValue={metadocSearchValue}
            onSearchChange={setMetadocSearchValue}
          />
        );

      case "loading":
        return <MetaDocLoadingScreen message="Loading Meta Document..." />;

      case "error":
        return (
          <MetaDocErrorScreen
            message="Processing Failed"
            detail={metadoc?.error_message}
            onAction={handleOpenDocuments}
          />
        );

      case "viewer": {
        if (!metadoc) {
          return <MetaDocLoadingScreen message="Loading Meta Document..." />;
        }
        if (!isSummariesMetadataEnabled || effectiveGroupingSource === "OPS") {
          return (
            <MetaDocErrorBoundary viewState={viewState} metadocId={metadoc.id}>
              <MetaDocViewerWorkspace
                key={metadoc.id}
                metadoc={metadoc}
                onDeletionStateChange={state.setIsPageDeletionInProgress}
                documentGroups={activeGroups}
                documentGroupTags={documentGroupTags}
                pageClassificationStats={computed.activePageStats}
                onRunAIModel={handleRunAIModel}
                isRunningAIModel={isRunningAIModel}
                isRunAiModelDisabled={computed.isRunAiModelDisabled}
                runAiModelDisabledReason={computed.runAiModelDisabledReason}
                lastCompletedAIJob={computed.lastCompletedAIJob}
                isRunAiModelReady={computed.isRunAiModelReady}
                isMetadocFetching={isMetadocFetching}
                groupingSource={effectiveGroupingSource}
                resolvedDownloadUrl={resolvedDownloadUrl}
                onGroupingSourceChange={handleGroupingSourceChange}
                canShowGroupingToggle={canShowGroupingToggle}
                onTabChange={state.setActiveTab}
              />
            </MetaDocErrorBoundary>
          );
        }
        const tabs = createTabs({
          metadoc,
          activeGroups,
          documentGroupTags,
          activePageStats: computed.activePageStats,
          onRunAIModel: handleRunAIModel,
          isRunningAIModel,
          isRunAiModelDisabled: computed.isRunAiModelDisabled,
          runAiModelDisabledReason: computed.runAiModelDisabledReason,
          lastCompletedAIJob: computed.lastCompletedAIJob,
          isRunAiModelReady: computed.isRunAiModelReady,
          isMetadocFetching,
          groupingSource: effectiveGroupingSource,
          resolvedDownloadUrl,
          onGroupingSourceChange: handleGroupingSourceChange,
          canShowGroupingToggle,
          onDeletionStateChange: state.setIsPageDeletionInProgress,
        });
        const activeTab = tabs.some((tab) => tab.id === state.activeTab)
          ? state.activeTab
          : "overview";

        return (
          <Tabs
            className="metadoc-tabs"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(tabId) => state.setActiveTab(tabId as TabId)}
          />
        );
      }

      case "preparing":
      default:
        return (
          <MetaDocLoadingScreen message="Meta Document is being prepared..." />
        );
    }
  };

  return (
    <div className={`metadoc-page ${pageClass}`}>
      {renderContent()}
      {medicalHistoryService && (
        <>
          <FutureMedicalMedicalHistoryReview service={medicalHistoryService} />
          <FMRxHistory service={medicalHistoryService} />
        </>
      )}
      <Documents
        fileId={parsedFileId}
        metaDoc={true}
        selectedDocumentIds={state.selectedDocumentIds}
        onSelectionChange={state.setSelectedDocumentIds}
      />
      <TaskModal
        isOpen={showTaskModal}
        onClose={handleCloseTaskModal}
        fileId={resolvedFileId}
        serviceId={currentFutureMedicalService?.service}
      />
      <CodeDirectoryDrawer />
      {resolvedFileId && (
        <BackgroundJobsDrawer
          filters={
            metadocId ? { metadoc_id: metadocId } : { file_id: resolvedFileId }
          }
        />
      )}
    </div>
  );
};

export default MetaDocPage;
